import React, { useEffect, useState } from 'react'
import { axiosClient } from 'scripts/api'
import { navigate } from 'gatsby'
import { cookies } from 'scripts/cookies'
import { Loading } from 'presentation/components/Loading'
import { getSubdomain } from 'scripts/getSubdomainForHost'
import { fullUrl } from 'scripts/fullUrl'
import { env } from 'scripts/env'
import { customBaseDomainFromHost } from 'scripts/customBaseDomain'
import { useTranslation } from 'react-i18next'
import { getTeam } from 'api/getTeam'
import { StringParam, useQueryParam } from 'use-query-params'
import { useBrand, useBrandLoading } from 'scripts/brand/BrandContext'

// subdomain として許可する文字（DNS label 相当の英数字とハイフン）。
// cookie `_locked_auth_subdomain` や API 由来の値を無検証で origin に埋め込むと
// open redirect / origin 汚染（例 `evil.com/?x=` のような値）になりうるため検証する。
const VALID_SUBDOMAIN = /^[a-z0-9-]+$/i

// 連れ戻しリダイレクト先（tenant の auth web origin）。
// custom domain では現在ドメイン配下の `{tenant}.auth.{base}` に向ける（LOCKED ドメインへ離脱させない）。
// 不正な subdomain（許可文字以外を含む）では null を返し、呼び出し側でリダイレクトしない。
export const tenantAuthOrigin = (subdomain: string): string | null => {
  if (!VALID_SUBDOMAIN.test(subdomain)) {
    return null
  }
  const base = customBaseDomainFromHost(window.location.hostname)
  if (base) {
    // 非標準ポート（dev / 検証の :5000 等）でアクセス中は port を引き継ぐ。
    // 落とすと cookie redirect が tenant auth origin に届かない。
    const port = window.location.port ? `:${window.location.port}` : ''
    return `${window.location.protocol}//${subdomain}.auth.${base}${port}`
  }
  return `${env.SELF_PROTCOL}://${subdomain}.${env.SELF_DOMAIN}:${env.SELF_PORT}`
}

export const Root = () => {
  const [loading, setLoading] = useState(true)
  const { t } = useTranslation()
  const brand = useBrand()
  // brand 解決前に DEFAULT / LOCKED の productDisplayName を描画しないよう、解決まで待つ（FOUC 防止）。
  const brandLoading = useBrandLoading()
  const [appClientKeyQueryParam] = useQueryParam('app_client_key', StringParam)

  useEffect(() => {
    const subdomain = getSubdomain(window.location.host)
    const sessionToken = cookies.get('session_token')
    const cookieSubdomain = cookies.get('_locked_auth_subdomain')

    if (subdomain === '' && typeof sessionToken === 'undefined') {
      const url = cookieSubdomain ? tenantAuthOrigin(cookieSubdomain) : null
      if (url) {
        window.location.replace(url)
        return
      } else {
        // cookie 無し / 不正な subdomain 値ではリダイレクトせず安全側（現状ページ表示）に倒す。
        setLoading(false)
        return
      }
    }

    ;(async () => {
      const postUrl = `${fullUrl(
        'api',
      )}/enduser_device_sessions/restore_subdomain`
      const params = { session_token: sessionToken }
      const { data } = await axiosClient.post(postUrl, params)
      const isValidSessionToken = typeof data.subdomain === 'string'

      if (subdomain === '' && !isValidSessionToken) {
        const url = cookieSubdomain ? tenantAuthOrigin(cookieSubdomain) : null
        if (url) {
          window.location.replace(url)
          return
        } else {
          // cookie 無し / 不正な subdomain 値ではリダイレクトせず安全側に倒す。
          setLoading(false)
          return
        }
      }

      const getTeamIdResponse = await getTeam(subdomain)
      const isValidSubdomain =
        getTeamIdResponse.status === 200 &&
        typeof getTeamIdResponse.data.team.id === 'number'

      if (isValidSubdomain && isValidSessionToken) {
        if (appClientKeyQueryParam && appClientKeyQueryParam !== '') {
          navigate(`/apps?app_client_key=${appClientKeyQueryParam}`, {
            replace: true,
          })
          return
        } else {
          navigate('/apps', { replace: true })
          return
        }
      } else if (isValidSubdomain && !isValidSessionToken) {
        if (appClientKeyQueryParam && appClientKeyQueryParam !== '') {
          navigate(`/login?app_client_key=${appClientKeyQueryParam}`, {
            replace: true,
          })
          return
        } else {
          navigate('/login', { replace: true })
          return
        }
      } else if (!isValidSubdomain && isValidSessionToken) {
        // data.subdomain（API 由来）も同様に検証する。不正値ならリダイレクトせず 404 扱い。
        const origin = tenantAuthOrigin(data.subdomain)
        if (origin) {
          window.location.replace(`${origin}/apps`)
          return
        }
        navigate('/404', { replace: true })
        return
      } else if (!isValidSubdomain && !isValidSessionToken) {
        navigate('/404', { replace: true })
        return
      } else {
        console.info('none of the above')
      }
    })()
  }, [])

  if (loading || brandLoading) {
    return (
      <main
        data-test="loadingRootPage"
        className="pb-20 pt-10 w-full flex justify-center"
      >
        <Loading />
      </main>
    )
  }

  return (
    <div
      className="flex items-center justify-center flex-col"
      data-test="pleaseLoginYourLoginPageLayout"
    >
      <p className="mt-32 text-locked font-bold text-2xl">
        {t('チームごとのログインURLからログインして下さい')}{' '}
      </p>
      <p className="mt-3 mb-10">
        {/* OEM: NEUTRAL_BRAND（productDisplayName=''）では「にログインすると、…」と文言が崩れるため、
            productName を含まない中立文言に分岐する（#390 と同方針。brandLoading は上で早期 return 済み）。 */}
        {brand.productDisplayName
          ? t('{{productName}}にログインすると、機能を使えるようになります。', {
              productName: brand.productDisplayName,
            })
          : t('ログインすると、機能を使えるようになります。')}{' '}
      </p>
      {/* OEM: partner ブランド時は LOCKED 向けの「よくある質問」を出さない（他画面の LockedHelpLink と同方針）。
          root は上部で loading/brandLoading を早期 return 済みのため、ここでは解決後の fallback だけ見ればよい。 */}
      {brand.fallback && (
        <button className="mt-10 rounded h-12 bg-gray-200 px-4">
          <div className="flex justify-center items-center">
            <p className="mr-3 text-gray-700">{t('よくある質問')} </p>
            <svg
              width="8"
              height="12"
              viewBox="0 0 8 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M2.00094 0L0.585938 1.415L5.17094 6L0.585938 10.585L2.00094 12L8.00094 6L2.00094 0Z"
                fill="#828282"
              ></path>
            </svg>
          </div>
        </button>
      )}
    </div>
  )
}
